
import { Component as VC } from 'vue/types/umd';
import { Component, Prop, Vue } from 'nuxt-property-decorator';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import FilmInfoTab, { IItemGroup } from './FilmInfoTab.vue';
import FilmMediaTab from './FilmMediaTab.vue';
import FilmMediaTabItem from './FilmMediaTabItem.vue';
import RecommendedAgeIcon from '~/components/UIComponents/RecommendedAgeIcon.vue';
import {
  ISanityDocuments,
  SanityDocumentSchema,
} from '~/types/sanity-documents';

interface ITabGroup {
  key: string;
  title: string;
  content: {
    component: VC;
    props: Record<string, any>;
  };
}

@Component({
  components: { RecommendedAgeIcon },
})
export default class FilmInfoTabs extends Vue {
  @Prop(Object) readonly doc!: ISanityDocuments[SanityDocumentSchema.FILM];

  tab = null;
  schemas = SanityDocumentSchema;

  get tabs() {
    const tabs: ITabGroup[] = [];

    if (this.castAndCrew.length) {
      tabs.push({
        key: 'cast-and-crew',
        title: this.$t('components.films.filmInfoTabs.castAndCrew') as string,
        content: {
          component: FilmInfoTab,
          props: {
            items: this.castAndCrew,
            class: `film-info-tab-group group-cast-and-crew`,
          },
        },
      });
    }
    if (this.details.length) {
      tabs.push({
        key: 'film-details',
        title: this.$t('components.films.filmInfoTabs.details') as string,
        content: {
          component: FilmInfoTab,
          props: {
            items: this.details,
            class: `film-info-tab-group group-film-details`,
          },
        },
      });
    }

    if (this.extraMedia) {
      tabs.push({
        key: 'extra-media',
        title: this.$t('components.films.filmInfoTabs.extras') as string,
        content: {
          component: FilmMediaTab,
          props: {
            items: Array.from(this.extraMedia.values()),
            class: `film-media-tab-group group-extra-media`,
          },
        },
      });
    }

    return tabs;
  }

  get castAndCrew(): IItemGroup[] {
    const castAndCrew = new Map<string, IItemGroup>();
    if (this.doc?.castMembers?.length) {
      const cast: string[] = [];
      this.doc.castMembers.forEach((member) => {
        const person = this.$sanityLookup.getDocumentFromReference<
          ISanityDocuments[SanityDocumentSchema.PERSON]
        >({
          _ref: member.person?._ref as string,
        })?.data;
        if (!person?.name) {
          return;
        }
        const nameParts = [];
        nameParts.push(person.name);
        if (member.characterName) {
          nameParts.push(member.characterName);
        }
        cast.push(nameParts.join(', '));
      });

      if (cast.length) {
        castAndCrew.set('cast', {
          title: this.$t('components.films.filmInfoTabs.cast') as string,
          items: cast,
        });
      }
    }

    if (this.doc?.crewMembers?.length) {
      this.doc.crewMembers.forEach((member) => {
        const person = this.$sanityLookup.getDocumentFromReference<
          ISanityDocuments[SanityDocumentSchema.PERSON]
        >({
          _ref: member.person?._ref as string,
        })?.data;
        const role = this.$sanityLookup.getDocumentFromReference<
          ISanityDocuments[SanityDocumentSchema.ROLE]
        >({
          _ref: member.role?._ref as string,
        })?.data;
        if (!role?.slug || !role?.title || !person?.name) {
          return;
        }

        const group = castAndCrew.get(role.slug) || {
          title: role.title,
          items: [],
        };

        group.items.push(person.name);
        if (
          role.titlePlural &&
          group.items.length > 1 &&
          group.title !== role.titlePlural
        ) {
          group.title = role.titlePlural;
        }

        castAndCrew.set(role.slug, group);
      });
    }

    return [...castAndCrew.values()];
  }

  get details() {
    // We have 9 fields to show in a fixed order, and
    // we want the fields to be grouped together in 3 columns
    // which stack in mobile. In wider displays, however,
    // we want item heights to grow as a row, so multi column
    // layout wont work. Additionally, items from one column
    // should not flow into another column in the case of
    // missing items, but we should not see gaps in the middle
    // of a column either.
    // We can use grid to achieve this but we need to
    // 1. preserve 3 items per column, with any placeholders
    //    at the end of the column
    // 2. override order

    // Add fields to their respective columns:
    let col1 = [];
    let col2 = [];
    let col3 = [];
    if (this.releaseYear) {
      col1.push({
        key: 'release-year',
        title: this.$t(
          'components.films.filmInfoTabs.detailsHeadings.released',
        ) as string,
        items: [this.releaseYear],
      });
    }

    if (this.genre) {
      col1.push({
        key: 'genre',
        title: this.$t(
          'components.films.filmInfoTabs.detailsHeadings.genre',
        ) as string,
        items: [this.genre],
      });
    }

    if (this.regionOfOrigin?.titleAbbrev) {
      col1.push({
        key: 'region-of-origin',
        title: this.$t(
          'components.films.filmInfoTabs.detailsHeadings.regionOfOrigin',
        ) as string,
        items: [this.regionOfOrigin.titleAbbrev],
      });
    }

    if (this.audioLanguages) {
      col2.push({
        key: 'audio-languages',
        title: this.$t(
          'components.films.filmInfoTabs.detailsHeadings.audioLanguages',
        ) as string,
        items: [this.audioLanguages],
      });
    }

    if (this.subtitles?.items.length) {
      col2.push({
        key: 'subtitles',
        title:
          this.subtitles?.title ||
          (this.$t(
            'components.films.filmInfoTabs.detailsHeadings.released',
          ) as string),
        items: this.subtitles?.items,
      });
    }

    if (this.maxResolution?.title) {
      col2.push({
        key: 'max-res',
        title: this.$t(
          'components.films.filmInfoTabs.detailsHeadings.maxAvailableResolution',
        ) as string,
        items: [this.maxResolution.title],
      });
    }

    if (this.maturityRating) {
      col3.push({
        key: 'maturity-rating',
        title: this.$t(
          'components.films.filmInfoTabs.detailsHeadings.maturityRating',
        ) as string,
        items: this.maturityRating,
      });
    }

    if (this.accessibility) {
      col3.push({
        key: 'accessibility',
        title: this.$t(
          'components.films.filmInfoTabs.detailsHeadings.accessibility',
        ) as string,
        items: [this.accessibility],
      });
    }

    if (this.mainContent?.contentDetails) {
      col3.push({
        key: 'content-details',
        title: this.$t(
          'components.films.filmInfoTabs.detailsHeadings.videoDetails',
        ) as string,
        items: [this.mainContent.contentDetails],
      });
    }

    // Create placeholders and add items to details array
    const l = 3;
    let itemsFromColsWithContent: IItemGroup[] = [];
    let itemsFromColsWithoutContent: IItemGroup[] = [];
    [col1, col2, col3].forEach((col) => {
      const placeholders: IItemGroup[] = [];
      const numPlaceholders = l - col.length;
      for (let i = 0; i < numPlaceholders; i++) {
        placeholders.push({ title: '', items: [] });
      }

      // separate fully empty columns so they can be placed at the end
      if (placeholders.length === l) {
        itemsFromColsWithoutContent = [
          ...itemsFromColsWithoutContent,
          ...placeholders,
        ];
      } else {
        itemsFromColsWithContent = [
          ...itemsFromColsWithContent,
          ...col,
          ...placeholders,
        ];
      }
    });

    const details: IItemGroup[] = [
      ...itemsFromColsWithContent,
      ...itemsFromColsWithoutContent,
    ];

    // Swap orders, using 0 based index:
    // 1 <-> 3
    // 2 <-> 6
    // 5 <-> 7
    details[1].order = 3;
    details[3].order = 1;
    details[2].order = 6;
    details[6].order = 2;
    details[5].order = 7;
    details[7].order = 5;

    return details;
  }

  get extraMedia() {
    const extraMedia = new Map<string, IItemGroup>();

    if (this.doc?.extraMedia?.length) {
      this.doc.extraMedia.forEach((mediaItem) => {
        const mediaItemDoc = this.$sanityLookup.getDocumentFromReference<
          ISanityDocuments[
            | SanityDocumentSchema.VIDEO
            | SanityDocumentSchema.FIGURE]
        >({ _ref: mediaItem?._ref })?.data;

        if (
          !(mediaItemDoc as ISanityDocuments[SanityDocumentSchema.VIDEO])
            ?.video &&
          !(mediaItemDoc as ISanityDocuments[SanityDocumentSchema.FIGURE])
            ?.image
        ) {
          return;
        }

        const groupId = mediaItemDoc?.mediaType?._ref || 'unTyped';
        const group = extraMedia.get(groupId) || {
          titleRefId: groupId,
          items: [] as IItemGroup['items'],
        };

        group.items.push({
          component: FilmMediaTabItem,
          props: {
            data: mediaItemDoc,
          },
        });

        extraMedia.set(groupId, group);
      });
    }

    return extraMedia.size ? extraMedia : undefined;
  }

  // Info fields for 'details' tab

  get accessibility() {
    if (!this.mainContent?.transcripts) {
      return '';
    }

    const accessibility: Record<string, string> = {};
    this.transcripts?.forEach((transcript) => {
      if (
        transcript.type?.slug &&
        transcript.type.slug.toLowerCase() === 'sdh'
      ) {
        return;
      }

      if (
        transcript.type?.titlePlural &&
        !accessibility[transcript.type.titlePlural]
      ) {
        accessibility[transcript.type.titlePlural] =
          transcript.type.titlePlural;
      }
    });

    return Object.values(accessibility).join(', ');
  }

  get audioLanguages() {
    if (!this.mainContent?.languagesAudio) {
      return '';
    }

    const languages: string[] = [];
    this.mainContent.languagesAudio.forEach((language) => {
      const doc = this.$sanityLookup.getDocumentFromReference<
        ISanityDocuments[SanityDocumentSchema.LANGUAGE]
      >({
        _ref: (language?._ref as string) || '',
      })?.data;
      doc?.title && languages.push(doc.title);
    });
    return languages.join(', ');
  }

  get genre() {
    const genres: string[] = [];
    this.doc?.genres?.forEach((genre) => {
      const doc = this.$sanityLookup.getDocumentFromReference<
        ISanityDocuments[SanityDocumentSchema.GENRE]
      >({
        _ref: genre?._ref,
      })?.data;
      doc?.title && genres.push(doc.title);
    });
    return genres.join(', ');
  }

  get mainContent() {
    if (!this.doc?.content?._ref) {
      return;
    }
    return this.$sanityLookup.getDocumentFromId<
      ISanityDocuments[SanityDocumentSchema.VIDEO]
    >({
      _id: this.doc.content._ref,
      _type: this.schemas.VIDEO,
    })?.data;
  }

  get maxResolution() {
    if (!this.mainContent?.maxResolution?._ref) {
      return;
    }
    return this.$sanityLookup.getDocumentFromId<
      ISanityDocuments[SanityDocumentSchema.RESOLUTION]
    >({
      _id: this.mainContent.maxResolution._ref as string,
      _type: this.schemas.RESOLUTION,
    })?.data;
  }

  get regionOfOrigin() {
    if (!this.mainContent?.regionOfOrigin?._ref) {
      return;
    }
    return this.$sanityLookup.getDocumentFromId<
      ISanityDocuments[SanityDocumentSchema.REGION]
    >({
      _id: this.mainContent.regionOfOrigin._ref as string,
      _type: this.schemas.REGION,
    })?.data;
  }

  get maturityRating() {
    const items = [];

    if (this.mainContent?.contentRating?.ratingMinAge) {
      items.push({
        component: RecommendedAgeIcon,
        props: {
          age: this.mainContent.contentRating.ratingMinAge,
          class:
            'font--overline font-weight-bold min-age-icon mr-2 d-flex align-center justify-center',
        },
      });
    }

    if (this.mainContent?.contentRating?.ratingDetails) {
      items.push(this.mainContent?.contentRating?.ratingDetails);
    }

    return items;
  }

  get releaseYear() {
    const dateString = this.doc?.releaseDate || '';

    if (!dateString) {
      return '';
    }

    return format(parseISO(dateString), 'Y');
  }

  get subtitles() {
    if (!this.mainContent?.transcripts) {
      return null;
    }

    const subtitles: string[] = [];
    let title = '';

    this.transcripts.forEach((transcript) => {
      if (
        !transcript.type?.slug ||
        !transcript.language?.title ||
        transcript.type.slug?.toLowerCase() !== 'sdh'
      ) {
        return;
      }
      if (!title && transcript.type.title) {
        title = transcript.type.title;
      }

      subtitles.push(transcript.language.title);
    });

    return {
      title,
      items: subtitles.length ? [subtitles.join(', ')] : [],
    };
  }

  get transcripts() {
    return (
      this.mainContent?.transcripts?.map((transcript) => ({
        _type: transcript._type,
        type: this.$sanityLookup.getDocumentFromReference<
          ISanityDocuments[SanityDocumentSchema.TRANSCRIPT_TYPE]
        >({
          _ref: transcript?.type?._ref,
        })?.data,
        language: this.$sanityLookup.getDocumentFromReference<
          ISanityDocuments[SanityDocumentSchema.LANGUAGE]
        >({
          _ref: transcript.language?._ref,
        })?.data,
      })) || []
    );
  }
}
